import { ApiError } from "@rhema/client";
import { Button, ButtonLink, cn, focusRing, TextField, UiLink } from "@rhema/ui";
import { type FormEvent, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { ServerSetting } from "../platform/ServerSetting";
import { useSession } from "../session/session";
import { AuthFrame, AuthHeading, AuthText, BackLink, ErrorNote } from "./SignInScreen";

const OFFLINE = "Rhema could not reach the server. Check your connection and try again.";
const linkClass = cn("rounded font-semibold text-blue no-underline", focusRing);

function messageOf(e: unknown): string {
  return e instanceof ApiError ? e.detail : OFFLINE;
}

/** Creating an account with an email address and a password, from "Sign up with email". */
export function SignUpPage() {
  const { api, signedIn } = useSession();
  const [params] = useSearchParams();
  const query = params.toString() ? `?${params.toString()}` : "";
  // From the pricing page's Choose Individual button. Sign-in then opens the Plan section.
  const signInPath = params.get("plan") === "individual" ? "/sign-in?plan=individual" : "/sign-in";
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (password.length < 8) {
      setError("Use at least 8 characters for your password.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await api.register({
        email: email.trim(),
        password,
        first_name: first.trim(),
        last_name: last.trim(),
      });
      // Contract v2: a new account must verify its email before it gets a session.
      if (res.verification_required) setSentTo(email.trim());
      else if (res.user) signedIn(res.user);
      else setError("That did not work. Try again.");
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setBusy(false);
    }
  };

  if (sentTo) {
    return (
      <AuthFrame>
        <AuthHeading>Check your email</AuthHeading>
        <AuthText>
          We sent a link to {sentTo}. Open it to verify your email address, then sign in. The link works for a
          limited time.
        </AuthText>
        <ButtonLink href={signInPath} size="lg">
          Go to sign in
        </ButtonLink>
      </AuthFrame>
    );
  }

  return (
    <AuthFrame busy={busy}>
      <BackLink href={`/sign-up${query}`}>Other ways to sign up</BackLink>
      <AuthHeading>Sign up with email</AuthHeading>
      <AuthText>We send a link to your email address to confirm it.</AuthText>
      {error && <ErrorNote>{error}</ErrorNote>}
      <form onSubmit={submit} className="flex flex-col gap-3">
        <div className="grid grid-cols-2 gap-2.5">
          <TextField
            label="First name"
            autoComplete="given-name"
            required
            maxLength={60}
            value={first}
            onChange={(e) => setFirst(e.target.value)}
          />
          <TextField
            label="Last name"
            autoComplete="family-name"
            maxLength={60}
            value={last}
            onChange={(e) => setLast(e.target.value)}
          />
        </div>
        <TextField
          label="Email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <TextField
          label="Password"
          type="password"
          autoComplete="new-password"
          required
          minLength={8}
          hint="At least 8 characters."
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <Button type="submit" size="lg" disabled={busy}>
          {busy ? "Creating your account" : "Create account"}
        </Button>
      </form>
      <p className="m-0 mt-1 text-center text-sm text-ink-secondary">
        Already have an account?{" "}
        <UiLink href={signInPath} className={linkClass}>
          Sign in
        </UiLink>
      </p>
    </AuthFrame>
  );
}

export function VerifyEmailPage() {
  const { api } = useSession();
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [state, setState] = useState<"working" | "done" | "failed">(token ? "working" : "failed");
  const [error, setError] = useState<string | null>(token ? null : "This link is missing its code.");
  // Verification links work once, so the request must not repeat when React re-runs effects.
  const sent = useRef(false);

  useEffect(() => {
    if (!token || sent.current) return;
    sent.current = true;
    api
      .verifyEmail(token)
      .then(() => setState("done"))
      .catch((e) => {
        setError(messageOf(e));
        setState("failed");
      });
  }, [api, token]);

  return (
    <AuthFrame busy={state === "working"}>
      {state === "working" && (
        <>
          <AuthHeading>Verifying your email</AuthHeading>
          <AuthText>This takes a moment.</AuthText>
        </>
      )}
      {state === "done" && (
        <>
          <AuthHeading>Your email is verified</AuthHeading>
          <AuthText>You can sign in to Rhema now.</AuthText>
          <ButtonLink href="/sign-in" size="lg">
            Sign in
          </ButtonLink>
        </>
      )}
      {state === "failed" && (
        <>
          <AuthHeading>That link did not work</AuthHeading>
          {error && <ErrorNote>{error}</ErrorNote>}
          <AuthText>
            Sign in with your email and password and we will send a new link if you need one.
          </AuthText>
          <ButtonLink href="/sign-in" size="lg">
            Go to sign in
          </ButtonLink>
        </>
      )}
    </AuthFrame>
  );
}

export function ForgotPasswordPage() {
  const { api } = useSession();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    // The answer is the same whether or not an account exists, so no one can test addresses here.
    await api.forgotPassword(email.trim()).catch(() => undefined);
    setBusy(false);
    setSent(true);
  };

  return (
    <AuthFrame busy={busy}>
      <AuthHeading>Reset your password</AuthHeading>
      {sent ? (
        <>
          <AuthText>
            If an account uses that email address, we have sent it a link to reset the password. Check your
            inbox, and your spam folder too.
          </AuthText>
          <ButtonLink href="/sign-in" size="lg">
            Back to sign in
          </ButtonLink>
        </>
      ) : (
        <>
          <AuthText>Enter the email address you sign in with and we will send you a link.</AuthText>
          <form onSubmit={submit} className="flex flex-col gap-3">
            <TextField
              label="Email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <Button type="submit" size="lg" disabled={busy}>
              {busy ? "Sending" : "Send the link"}
            </Button>
          </form>
          <p className="m-0 mt-1 text-center text-sm">
            <UiLink href="/sign-in" className={linkClass}>
              Back to sign in
            </UiLink>
          </p>
        </>
      )}
    </AuthFrame>
  );
}

export function ResetPasswordPage() {
  const { api } = useSession();
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(token ? null : "This link is missing its code.");
  const [done, setDone] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (password.length < 8) return setError("Use at least 8 characters for your password.");
    if (password !== confirm) return setError("The two passwords do not match.");
    setBusy(true);
    setError(null);
    try {
      await api.resetPassword(token, password);
      setDone(true);
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <AuthFrame>
        <AuthHeading>Your password is changed</AuthHeading>
        <AuthText>Sign in with your new password.</AuthText>
        <ButtonLink href="/sign-in" size="lg">
          Sign in
        </ButtonLink>
      </AuthFrame>
    );
  }

  return (
    <AuthFrame busy={busy}>
      <AuthHeading>Choose a new password</AuthHeading>
      {error && <ErrorNote>{error}</ErrorNote>}
      <form onSubmit={submit} className="flex flex-col gap-3">
        <TextField
          label="New password"
          type="password"
          autoComplete="new-password"
          required
          minLength={8}
          hint="At least 8 characters."
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <TextField
          label="Type it again"
          type="password"
          autoComplete="new-password"
          required
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
        />
        <Button type="submit" size="lg" disabled={busy || !token}>
          {busy ? "Saving" : "Save the new password"}
        </Button>
      </form>
      <p className="m-0 mt-1 text-center text-sm">
        <UiLink href="/forgot-password" className={linkClass}>
          Ask for a new link
        </UiLink>
      </p>
    </AuthFrame>
  );
}

/** Where the PIN reset email links to. SBI puts a one-time token in the link. */
export function PinResetPage() {
  const { api } = useSession();
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [pin, setPin] = useState("");
  const [again, setAgain] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(token ? null : "This link is missing its code.");
  const [done, setDone] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (pin.length !== 6) return setError("Your PIN must be 6 digits.");
    if (pin !== again) return setError("The two PINs do not match.");
    setBusy(true);
    setError(null);
    try {
      await api.phonePinReset(token, pin);
      setDone(true);
    } catch (err) {
      setError(messageOf(err));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <AuthFrame>
        <AuthHeading>Your PIN is changed</AuthHeading>
        <AuthText>Sign in with your phone number. When Rhema asks for your PIN, enter the new one.</AuthText>
        <ButtonLink href="/sign-in" size="lg">
          Sign in
        </ButtonLink>
      </AuthFrame>
    );
  }

  return (
    <AuthFrame busy={busy}>
      <AuthHeading>Choose a new PIN</AuthHeading>
      <AuthText>Rhema asks for this PIN when your number signs in on a new device.</AuthText>
      {error && <ErrorNote>{error}</ErrorNote>}
      <form onSubmit={submit} className="flex flex-col gap-3">
        <TextField
          label="New 6-digit PIN"
          type="password"
          inputMode="numeric"
          autoComplete="new-password"
          maxLength={6}
          required
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
        />
        <TextField
          label="Type it again"
          type="password"
          inputMode="numeric"
          autoComplete="new-password"
          maxLength={6}
          required
          value={again}
          onChange={(e) => setAgain(e.target.value.replace(/\D/g, "").slice(0, 6))}
        />
        <Button type="submit" size="lg" disabled={busy || !token || pin.length !== 6}>
          {busy ? "Saving" : "Save the new PIN"}
        </Button>
      </form>
    </AuthFrame>
  );
}

export function OwnServerPage() {
  const { platform } = useSession();
  if (platform.native) {
    return (
      <AuthFrame>
        <AuthHeading>Connect to your own server</AuthHeading>
        <AuthText>
          If your organisation runs its own Rhema server, enter its address here. Otherwise keep the Rhema
          server.
        </AuthText>
        <ServerSetting platform={platform} />
        <ButtonLink href="/sign-in" size="lg" variant="tonal">
          Back to sign in
        </ButtonLink>
      </AuthFrame>
    );
  }
  return (
    <AuthFrame>
      <AuthHeading>Host Rhema yourself</AuthHeading>
      <AuthText>
        Organisations can run Rhema on their own servers, so their accounts and encrypted messages stay on
        hardware they control.
      </AuthText>
      <AuthText>
        The documentation for setting up your own server is coming soon. Until then, sign in with a Silicon
        Blast account to use Rhema.
      </AuthText>
      <ButtonLink href="/sign-in" size="lg" variant="tonal">
        Back to sign in
      </ButtonLink>
    </AuthFrame>
  );
}
